import { Component, Host, h, Element, Prop, Watch, Event, EventEmitter, State, Fragment, Listen } from "@stencil/core";

import {
  StzhChipselectItem,
  StzhChipselectChangeEvent,
  // StzhDropdownChangeEvent
} from "../../index";

import { StzhInputDescription } from "../stzh-input/stzh-input-description";

// import { hasSlot } from "../../utils/utils";
import { StzhLocaleComponent } from "../../index";

let chipselectCounter = 0;

/**
 * @slot - Slot for `stzh-checkbox` elements
 * @slot description - Slot for description
 * @slot description-long - Slot for long description (in popover)
 * @slot description-long-title - Slot for long description title (in popover) / instead of descriptionLongTitle or description property
 * @slot error - Slot for error
 */
@Component({
  tag: "stzh-chipselect",
  styleUrl: "stzh-chipselect.scss",
  scoped: true,
})
export class StzhChipselect {
  /** Translation strings */
  @Prop() localization: StzhLocaleComponent;

  /** The list of chips */
  @Prop() data: string | StzhChipselectItem[] = [];
  private _data: StzhChipselectItem[] = [];
  // private _dataDropdown: any[] = [];

  /** The name of the input element */
  @Prop({ reflect: true }) name: string;

  /** Label */
  @Prop() label: string = "";

  /** Whether label is hidden. */
  @Prop() labelHidden: boolean = false;

  /** Multiple */
  @Prop({ mutable: true }) multiple: boolean = false;

  /** Value */
  @Prop({ mutable: true }) value: string | string[] = "";
  private _value: string[] = [];

  /** Default value (used by reset) */
  @Prop({ mutable: true }) defaultValue: string | string[] = "";

  /** Description message (use description slot as alternative) */
  @Prop() description: string;

  /** Long description message appearing in a popover (use description-long slot as alternative) */
  @Prop() descriptionLong: string;

  /**
   * **Do not use this property. It will be deprecated with the next major release.**
   * Long description title appearing in a popover (use description-long-title slot as or description property alternative) */
  @Prop() descriptionLongTitle: string;

  /** One or multiple error message (use error slot as alternative) */
  @Prop() error: string | string[];
  private _error: string[];

  /** Invalid status */
  @Prop({ reflect: true }) invalid: boolean = false;

  /** Required status */
  @Prop({ reflect: true }) required: boolean = false;

  /** Whether all chips are disabled or not */
  @Prop({ reflect: true }) disabled: boolean = false;

  /** Hide `(optional)` label (or use `required` inside form to hide it) */
  @Prop({ reflect: true }) hideOptional: boolean = false;

  /** Id for element which describes the input (this will be overwritten if description prop or slot is used, used by stzh-radiogroup) */
  @Prop({ attribute: "a11y-describedby" }) a11yDescribedby: string = "";

  @State() activeFocusOptionIndex: number = 0;

  @Element() element: HTMLStzhChipselectElement;

  /** Change event */
  @Event() stzhChange: EventEmitter<StzhChipselectChangeEvent>;

  @Listen("reset", { target: "document" })
  resetListener(event: Event) {
    if ((event.target as HTMLElement).contains(this.element)) {
      requestAnimationFrame(() => {
        this.handleReset();
      });
    }
  }

  @Watch("value")
  valueWatcher(newValue: string[] | string) {
    if (typeof newValue === "string") {
      try {
        const parsedValue = JSON.parse(newValue); // since parsing a number like string (i.e. "1") will return a number and parsing a boolean like string (i.e. "true") will return a boolean we need to check if the parsed value is indeed an array.
        if (Array.isArray(parsedValue)) {
          this._value = parsedValue;
        } else {
          this._value = newValue ? [newValue] : [];
        }
      } catch (ex) {
        this._value = newValue ? [newValue] : [];
      }
    } else {
      this._value = newValue ? newValue : [];
    }
  }

  @Watch("data")
  dataWatcher(newValue: StzhChipselectItem[] | string) {
    if (typeof newValue === "string") {
      this._data = JSON.parse(newValue);
    } else {
      this._data = newValue;
    }

    this._data.map(dataEntry => {
      if (typeof dataEntry.value !== "string") {
        throw new TypeError(
          `${dataEntry.value} is not a string. The data prop only accepts arrays (or stringified arrays), where the value property is a string. Consult the StzhChipselectItem type definition for more information.`,
        );
      }
    });
    // this._dataDropdown = this._data.map((data) => {
    //   return {
    //     text: data.counter
    //       ? `${data.label} (${data.counter})`
    //       : data.label,
    //     value: data.value
    //   }
    // });
  }

  @Watch("error")
  errorWatcher(newValue: string | string[]) {
    if (typeof newValue === "string") {
      try {
        this._error = JSON.parse(newValue);
      } catch (e) {
        if (newValue) {
          this._error = [newValue];
        } else {
          this._error = [];
        }
      }
    } else if (newValue) {
      this._error = newValue;
    } else {
      this._error = [];
    }
  }

  private id: string;
  private renderPromiseResolve: (value?: unknown) => void;

  private waitForNextRender() {
    return new Promise(resolve => (this.renderPromiseResolve = resolve));
  }

  private handleReset = async () => {
    this.value = this.defaultValue;

    // this.stzhChange.emit({
    //   component: "stzh-chipselect",
    //   value: this.value
    // });
  };

  private handleChipClick = async (data: StzhChipselectItem, index: number) => {
    if (this.multiple) {
      if (data.value === "" && this._value.length > 0 && !(this._value.length === 1 && this._value[0] === "")) {
        this.value = [];
      } else if (data.value !== "") {
        this.value = this._value.includes(data.value)
          ? this._value.filter(value => value !== data.value)
          : [...this._value, data.value];
      }
    } else {
      this.value = data.value;
    }

    this.activeFocusOptionIndex = index;

    await this.waitForNextRender();

    this.stzhChange.emit({
      component: "stzh-chipselect",
      value: this.multiple ? this._value : this._value[0],
    });
  };

  // private handleDropdownChange = async (event: CustomEvent<StzhDropdownChangeEvent>) => {
  //   this.value = Array.isArray(event.detail.value) ? event.detail.value[0] : event.detail.value;

  //   this.activeFocusOptionIndex = this._data.findIndex(data => {
  //     return data.value === this.value;
  //   });

  //   await this.waitForNextRender();

  //   this.stzhChange.emit({
  //     component: "stzh-chipselect",
  //     value: this.value
  //   })
  // }

  private handleKeydown = (event: KeyboardEvent) => {
    let newActiveIndex: number = null;
    const activeOptionIndex = this.activeFocusOptionIndex;

    if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
      event.preventDefault();

      if (activeOptionIndex > 0) {
        newActiveIndex = activeOptionIndex - 1;
      }
    } else if (event.key === "ArrowDown" || event.key === "ArrowRight") {
      event.preventDefault();

      if (activeOptionIndex < this._data.length - 1) {
        newActiveIndex = activeOptionIndex + 1;
      }
    } else if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      if (this.multiple) {
        if (
          this._data[activeOptionIndex].value === "" &&
          this._value.length > 0 &&
          !(this._value.length === 1 && this._value[0] === "")
        ) {
          this.value = [];
        } else if (this._data[activeOptionIndex].value !== "") {
          this.value = this._value.includes(this._data[activeOptionIndex].value)
            ? this._value.filter(value => value !== this._data[activeOptionIndex].value)
            : [...this._value, this._data[activeOptionIndex].value];
        }
      } else {
        this.value = this._data[activeOptionIndex].value; //HERE
      }
      this.stzhChange.emit({
        component: "stzh-chipselect",
        value: this.value,
      });
    }

    if (newActiveIndex !== null) {
      this.activeFocusOptionIndex = newActiveIndex;
    }
  };

  componentDidRender() {
    if (this.renderPromiseResolve) {
      this.renderPromiseResolve();
    }
  }

  async componentWillLoad() {
    this.id = `stzh-chipselect-${chipselectCounter++}`;

    this.dataWatcher(this.data);
    this.errorWatcher(this.error);
    this.valueWatcher(this.value);

    this.defaultValue = this.defaultValue || this.value;

    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "chipselect");
    }
  }

  render() {
    const descriptionUsed = !!this.description;
    const descriptionLongUsed = !!this.descriptionLong;
    const errorUsed = !!this.error;

    const classes = {
      "stzh-chipselect": true,
      "stzh-chipselect--has-description": descriptionUsed,
      "stzh-chipselect--has-description-long": descriptionLongUsed,
      "stzh-chipselect--has-error": errorUsed,
      "stzh-chipselect--is-invalid": this.invalid || errorUsed,
      "stzh-chipselect--is-disabled": this.disabled,
      "stzh-chipselect--is-required": this.required,
      "stzh-chipselect--has-label-hidden": this.labelHidden,
    };

    return (
      <Host is-invalid={this.invalid || errorUsed}>
        <div class={classes}>
          {this.multiple ? (
            <Fragment>
              {this._value.map(value => (
                <input type="hidden" name={this.name} value={value} />
              ))}
            </Fragment>
          ) : (
            <input type="hidden" name={this.name} value={this.value} />
          )}

          <label class="stzh-chipselect__label" htmlFor={this.id}>
            {this.label ? this.label : <slot></slot>}
            {!this.hideOptional && (
              <span class="stzh-chipselect__marker">
                <span class="stzh-chipselect__marker-symbol" aria-hidden="true">
                  {this.required
                    ? this.localization.$globals.requiredFieldMarker
                    : this.localization.$globals.optionalFieldMarker}
                </span>
                <span class="stzh-chipselect__marker-text">
                  {this.required
                    ? this.localization.$globals.requiredFieldText
                    : this.localization.$globals.optionalFieldText}
                </span>
              </span>
            )}
          </label>

          {this._data.length > 0 && (
            <Fragment>
              <div
                id={this.id}
                class="stzh-chipselect__chips"
                role="listbox"
                tabindex="0"
                aria-multiselectable={this.multiple ? "true" : "false"}
                aria-activedescendant={`${this.id}-option-${this.activeFocusOptionIndex}`}
                aria-describedby={`${this.id}-description ${this.a11yDescribedby}`}
                onKeyDown={this.handleKeydown}
              >
                {this._data.map((data, index) => (
                  <stzh-chip
                    role="option"
                    class={{
                      "stzh-chipselect__chip": true,
                      "is-focused": this.activeFocusOptionIndex === index,
                    }}
                    label={data.label}
                    counter={data.counter}
                    icon={data.icon}
                    type={data.type}
                    inverted={data.inverted}
                    variant={data.variant}
                    size={data.size || "small"}
                    disabled={this.disabled || data.disabled}
                    active={
                      this._value.includes(data.value) ||
                      (!this.multiple && !data.value && (this._value.length === 0 || this._value[0] === "")) ||
                      (!data.value &&
                        this.multiple &&
                        (this._value.length === 0 || (this._value.length === 1 && this._value[0] === "")))
                    }
                    aria-selected={
                      this._value.includes(data.value) ||
                      (!this.multiple && !data.value && (this._value.length === 0 || this._value[0] === "")) ||
                      (!data.value &&
                        this.multiple &&
                        (this._value.length === 0 || (this._value.length === 1 && this._value[0] === "")))
                        ? "true"
                        : "false"
                    }
                    id={`${this.id}-option-${index}`}
                    a11yTabindex="-1"
                    onClick={() => this.handleChipClick(data, index)}
                  ></stzh-chip>
                ))}
              </div>

              <StzhInputDescription
                classPrefix="stzh-chipselect"
                id={`${this.id}-description`}
                error={this._error}
                description={this.description}
                descriptionLong={this.descriptionLong}
                descriptionLongTitle={this.descriptionLongTitle}
                descriptionLongUsed={descriptionLongUsed}
                moreInfoButtonLabel={this.localization.$globals.moreInfoButtonLabel}
              />

              {/* <stzh-dropdown
                class="stzh-chipselect__dropdown"
                multiple={this.multiple}
                disabled={this.disabled}
                error={this._error}
                description={this.description}
                descriptionLong={this.descriptionLong}
                required={this.required}
                invalid={this.invalid}
                items={[this.value]}
                label={this.label}
                labelHidden={this.labelHidden}
                options={this._dataDropdown}
                hideOptional={this.hideOptional}
                onStzhChange={this.handleDropdownChange}
              ></stzh-dropdown> */}
            </Fragment>
          )}
        </div>
      </Host>
    );
  }
}
